// src/pages/Dashboard/DashboardHome/components/RevenueByServiceChart.tsx
import React, { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, LabelList } from "recharts";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import ContentLoader from "react-content-loader";
import "./RevenueByServiceChart.css";

interface RevenueByServiceChartProps {
  month: number;
  year: number;
}

interface RevenueByType {
  values: {
    SERVICO: number;
    ESPACO_COMODIDADE: number;
    OUTRO: number;
  };
  percentages: {
    SERVICO: number;
    ESPACO_COMODIDADE: number;
    OUTRO: number;
  };
}

interface ChartData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  formattedValue: string;
}

const TYPE_LABELS: Record<string, string> = {
  SERVICO: "Serviços",
  ESPACO_COMODIDADE: "Espaços",
  OUTRO: "Outros",
};

const TYPE_COLORS: Record<string, string> = {
  SERVICO: "#8B5CF6",
  ESPACO_COMODIDADE: "#06B6D4",
  OUTRO: "#F59E0B",
};

function transformRevenueData(raw: any): ChartData[] {
  const revenueByType: RevenueByType = raw?.revenueByProductType;
  if (!revenueByType) return [];

  const entries = Object.entries(revenueByType.values)
    .map(([key, value]) => ({
      name: TYPE_LABELS[key] || key,
      value: value as number,
      percentage: revenueByType.percentages[key as keyof typeof revenueByType.percentages] || 0,
      color: TYPE_COLORS[key] || "#94A3B8",
      formattedValue: `R$ ${(value as number).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    }))
    .sort((a, b) => b.value - a.value);

  return entries;
}

async function fetchRevenueData(coworkingId: string, token: string, month: number, year: number): Promise<ChartData[]> {
  const path = `/coworking/${encodeURIComponent(coworkingId)}/dashboard?month=${month}&year=${year}`;
  const resp = await api.get(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  return transformRevenueData(raw);
}

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload as ChartData | undefined;
    if (!data) return null;
    return (
      <div className="chart-tooltip">
        <p className="tooltip-label">{data.name}</p>
        <p className="tooltip-value">
          R$ {data.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
        </p>
        <p className="tooltip-percentage">{data.percentage.toFixed(1)}%</p>
      </div>
    );
  }
  return null;
};

const RevenueByServiceChart: React.FC<RevenueByServiceChartProps> = ({ month, year }) => {
  const userContext = useUser();
  const userData: any = userContext?.userData;
  const token = userData?.auth;
  const coworkingId = userData?.coworkingId ?? null;
  
  const lastSuccessfulDataRef = useRef<ChartData[]>([]);

  const { data: chartData, isLoading: initialLoading, isFetching, error } = useQuery({
    queryKey: ['revenueByService', coworkingId, month, year],
    queryFn: () => fetchRevenueData(coworkingId!, token!, month, year),
    enabled: !!coworkingId && !!token,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (chartData && chartData.length > 0) {
      lastSuccessfulDataRef.current = chartData;
    }
  }, [chartData]);

  const displayData = (chartData && chartData.length > 0) ? chartData : lastSuccessfulDataRef.current;
  const hasData = displayData.length > 0;
  const isLoading = initialLoading && !hasData;

  // Calculate total for showing in empty state
  const totalRevenue = displayData.reduce((sum, d) => sum + d.value, 0);

  if (isLoading) {
    return (
      <div className="chart-skeleton">
        <ContentLoader
          speed={2}
          width="100%"
          height={180}
          backgroundColor="#f3f3f3"
          foregroundColor="#ecebeb"
        >
          <rect x="0" y="10" rx="4" ry="4" width="80%" height="40" />
          <rect x="0" y="60" rx="4" ry="4" width="60%" height="40" />
          <rect x="0" y="110" rx="4" ry="4" width="40%" height="40" />
        </ContentLoader>
      </div>
    );
  }

  if (error && !hasData) {
    return <div className="chart-error">Erro ao carregar dados</div>;
  }

  if (totalRevenue === 0) {
    return (
      <div className="chart-empty">
        <p>Nenhuma receita neste período</p>
        <span>Selecione outro mês ou adicione inscrições</span>
      </div>
    );
  }

  return (
    <div className={`revenue-chart-container bar-chart ${isFetching && !initialLoading ? 'refetching' : ''}`}>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart
          data={displayData}
          layout="vertical"
          margin={{ top: 5, right: 80, left: 10, bottom: 5 }}
        >
          <XAxis type="number" hide />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={80}
            tick={{ fontSize: 13, fill: '#64748B' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Bar 
            dataKey="value" 
            radius={[0, 6, 6, 0]}
            barSize={32}
          >
            {displayData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            <LabelList 
              dataKey="formattedValue" 
              position="right" 
              style={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Legend */}
      <div className="bar-chart-legend">
        {displayData.map((entry, index) => (
          <div key={index} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: entry.color }} />
            <span className="legend-name">{entry.name}</span>
            <span className="legend-pct">{entry.percentage.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevenueByServiceChart;
